import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  ArrowLeft,
  ClipboardCheck,
  MapPin,
  Calendar,
  Clock,
  User,
  Play,
  CheckCircle,
  Shield,
  XCircle,
  Wrench,
  FileText,
  History,
  AlertTriangle,
  Target,
  Link2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryLabels = {
  corrective: 'Corrective',
  preventive: 'Preventive',
  improvement: 'Improvement',
  compliance: 'Compliance',
  investigation: 'Investigation',
};

const categoryColors = {
  corrective: 'bg-red-100 text-red-700 border-red-300',
  preventive: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  improvement: 'bg-sky-100 text-sky-700 border-sky-300',
  compliance: 'bg-purple-100 text-purple-700 border-purple-300',
  investigation: 'bg-amber-100 text-amber-700 border-amber-300',
};

const priorityColors = {
  critical: 'bg-red-100 text-red-700 border-red-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  medium: 'bg-amber-100 text-amber-700 border-amber-300',
  low: 'bg-slate-100 text-slate-600 border-slate-300',
};

const statusColors = {
  open: 'bg-sky-100 text-sky-700 border-sky-300',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-300',
  blocked: 'bg-red-100 text-red-700 border-red-300',
  completed: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  verified: 'bg-purple-100 text-purple-700 border-purple-300',
  closed: 'bg-slate-100 text-slate-600 border-slate-300',
};

const sourceTypeLabels = {
  site_visit: 'Site Visit',
  maintenance: 'Maintenance',
  outage: 'Outage',
  monitoring: 'Monitoring',
  audit: 'Audit',
  manual: 'Manual',
};

export default function ActionPlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [closureNotes, setClosureNotes] = useState('');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [woAssignee, setWoAssignee] = useState('');

  const fetchAction = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/action-plans/${id}`);
      setAction(response.data);
    } catch (error) {
      console.error('Failed to fetch action:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAction();
  }, [fetchAction]);

  const handleStart = async () => {
    try {
      await axios.post(`${API}/action-plans/${id}/start`);
      fetchAction();
    } catch (error) {
      console.error('Failed to start action:', error);
    }
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/action-plans/${id}/complete`, { closure_notes: closureNotes });
      setShowCompleteDialog(false);
      fetchAction();
    } catch (error) {
      console.error('Failed to complete action:', error);
    }
  };

  const handleVerify = async () => {
    try {
      await axios.post(`${API}/action-plans/${id}/verify`, { verification_notes: verificationNotes });
      setShowVerifyDialog(false);
      fetchAction();
    } catch (error) {
      console.error('Failed to verify action:', error);
    }
  };

  const handleClose = async () => {
    try {
      await axios.post(`${API}/action-plans/${id}/close`);
      fetchAction();
    } catch (error) {
      console.error('Failed to close action:', error);
    }
  };

  const handleConvertToWorkOrder = async () => {
    try {
      const response = await axios.post(`${API}/action-plans/${id}/convert-to-workorder`, {
        assigned_to: woAssignee || action.owner,
        tasks: [
          { id: '1', description: action.title, completed: false },
          { id: '2', description: 'Document completion', completed: false },
        ],
      });
      setShowConvertDialog(false);
      toast.success(`Work Order ${response.data.wo_number} created successfully!`);
      fetchAction();
    } catch (error) {
      console.error('Failed to convert to work order:', error);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  if (!action) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Action plan not found</p>
          <Button asChild className="mt-4">
            <Link to="/action-plans">Back to Action Plans</Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const isOverdue = action.status !== 'closed' && action.status !== 'verified' && action.due_date < new Date().toISOString().split('T')[0];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/action-plans" data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-heading text-2xl font-bold text-slate-900">{action.action_number}</h1>
                <Badge variant="outline" className={cn(priorityColors[action.priority])}>
                  {action.priority.toUpperCase()}
                </Badge>
                <Badge variant="outline" className={cn(categoryColors[action.category])}>
                  {categoryLabels[action.category]}
                </Badge>
                <Badge variant="outline" className={cn(statusColors[action.status])}>
                  {action.status.replace('_', ' ').toUpperCase()}
                </Badge>
                {isOverdue && (
                  <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300">
                    OVERDUE
                  </Badge>
                )}
              </div>
              <p className="text-slate-500 mt-1">{action.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {action.status === 'open' && (
              <Button onClick={handleStart} data-testid="start-btn">
                <Play className="w-4 h-4 mr-2" />
                Start
              </Button>
            )}
            {action.status === 'in_progress' && (
              <Button onClick={() => setShowCompleteDialog(true)} data-testid="complete-btn">
                <CheckCircle className="w-4 h-4 mr-2" />
                Complete
              </Button>
            )}
            {action.status === 'completed' && (
              <Button onClick={() => setShowVerifyDialog(true)} variant="outline" data-testid="verify-btn">
                <Shield className="w-4 h-4 mr-2" />
                Verify
              </Button>
            )}
            {action.status === 'verified' && (
              <Button onClick={handleClose} variant="outline" data-testid="close-btn">
                <XCircle className="w-4 h-4 mr-2" />
                Close
              </Button>
            )}
            {!action.converted_to_wo_id && action.status !== 'closed' && (
              <Button variant="outline" onClick={() => setShowConvertDialog(true)} data-testid="convert-wo-btn">
                <Wrench className="w-4 h-4 mr-2" />
                Convert to WO
              </Button>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">Site</span>
              </div>
              <p className="font-medium text-slate-900">{action.site}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <User className="w-4 h-4" />
                <span className="text-sm">Owner</span>
              </div>
              <p className="font-medium text-slate-900">{action.owner}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm">Due Date</span>
              </div>
              <p className={cn('font-medium', isOverdue ? 'text-red-600' : 'text-slate-900')}>{action.due_date}</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-sm">SLA</span>
              </div>
              <p className="font-medium text-slate-900">{action.sla_hours} hours</p>
            </CardContent>
          </Card>
        </div>

        {/* Description */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-100 pb-4">
            <CardTitle className="font-heading text-lg">Description</CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            <p className="text-slate-600">{action.description}</p>
            {action.linked_asset_names?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-500 mb-2">Linked Assets</p>
                <div className="flex flex-wrap gap-2">
                  {action.linked_asset_names.map((name, i) => (
                    <Badge key={i} variant="outline">{name}</Badge>
                  ))}
                </div>
              </div>
            )}
            {action.root_cause_tags?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-medium text-slate-500 mb-2">Root Cause Tags</p>
                <div className="flex flex-wrap gap-2">
                  {action.root_cause_tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="bg-slate-100">{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="details" className="space-y-4">
          <TabsList>
            <TabsTrigger value="details" data-testid="tab-details">
              <FileText className="w-4 h-4 mr-2" />
              Details
            </TabsTrigger>
            <TabsTrigger value="audit" data-testid="tab-audit">
              <History className="w-4 h-4 mr-2" />
              Audit Trail ({action.audit_trail?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Source Info */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Link2 className="w-5 h-5" />
                    Source Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Source Type</span>
                    <Badge variant="outline">{sourceTypeLabels[action.source_type]}</Badge>
                  </div>
                  {action.source_reference_id && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Reference ID</span>
                      <span className="text-sm font-mono text-slate-900">{action.source_reference_id}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Risk Level</span>
                    <Badge variant="outline" className={cn(
                      action.risk_level === 'high' ? 'bg-red-100 text-red-700 border-red-300' :
                      action.risk_level === 'medium' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                      'bg-emerald-100 text-emerald-700 border-emerald-300'
                    )}>
                      {action.risk_level.toUpperCase()}
                    </Badge>
                  </div>
                  {action.reviewer && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Reviewer</span>
                      <span className="text-sm text-slate-900">{action.reviewer}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Closure Info */}
              <Card className="border-slate-200">
                <CardHeader className="border-b border-slate-100 pb-4">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Closure Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-3">
                  {action.closure_notes ? (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Closure Notes</p>
                      <p className="text-slate-900">{action.closure_notes}</p>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No closure notes yet</p>
                  )}
                  {action.verified_by && (
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-sm text-slate-500">Verified By</span>
                      <span className="text-sm text-slate-900">{action.verified_by}</span>
                    </div>
                  )}
                  {action.verified_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Verified At</span>
                      <span className="text-sm text-slate-900">{new Date(action.verified_at).toLocaleString()}</span>
                    </div>
                  )}
                  {action.closed_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Closed At</span>
                      <span className="text-sm text-slate-900">{new Date(action.closed_at).toLocaleString()}</span>
                    </div>
                  )}
                  {action.converted_to_wo_id && (
                    <div className="p-3 bg-sky-50 rounded-lg mt-4">
                      <p className="text-sm font-medium text-sky-800">Converted to Work Order</p>
                      <Link to="/work-orders" className="text-sm text-sky-600 hover:underline">
                        View Work Orders →
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="audit">
            <Card className="border-slate-200">
              <CardHeader className="border-b border-slate-100 pb-4">
                <CardTitle className="font-heading text-lg">Audit Trail</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                {action.audit_trail?.length === 0 ? (
                  <p className="text-slate-500">No audit entries</p>
                ) : (
                  <div className="space-y-4">
                    {action.audit_trail?.map((entry, i) => (
                      <div key={i} className="flex gap-4 pb-4 border-b border-slate-100 last:border-0">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <History className="w-4 h-4 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{entry.user}</span>
                            <Badge variant="outline" className="text-xs">{entry.action}</Badge>
                          </div>
                          <p className="text-sm text-slate-600 mt-1">{entry.details}</p>
                          <p className="text-xs text-slate-400 mt-1">{new Date(entry.timestamp).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Complete Dialog */}
        <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Complete Action</DialogTitle>
              <DialogDescription>Provide closure notes and evidence.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Closure Notes</Label>
                <Textarea
                  value={closureNotes}
                  onChange={(e) => setClosureNotes(e.target.value)}
                  placeholder="Describe the resolution..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancel</Button>
              <Button onClick={handleComplete}>Complete Action</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Verify Dialog */}
        <Dialog open={showVerifyDialog} onOpenChange={setShowVerifyDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Verify Action</DialogTitle>
              <DialogDescription>Confirm the action has been properly completed.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Verification Notes</Label>
                <Textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add verification notes..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVerifyDialog(false)}>Cancel</Button>
              <Button onClick={handleVerify}>Verify Action</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Convert to Work Order Dialog */}
        <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Convert to Work Order</DialogTitle>
              <DialogDescription>Create a work order from this action plan.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Assign To</Label>
                <Input
                  value={woAssignee}
                  onChange={(e) => setWoAssignee(e.target.value)}
                  placeholder={action.owner}
                />
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  This will create a work order for <strong>{action.title}</strong> at <strong>{action.site}</strong>.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConvertDialog(false)}>Cancel</Button>
              <Button onClick={handleConvertToWorkOrder}>Create Work Order</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
